import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  getFlavourFeatures,
  getFlavourType,
  type FlavourFeatures,
  type FlavourType,
} from '@/flavours';
import { useTheme } from './ThemeProvider';
import { useSiteConfig, useAuthStore, type DashboardSettings } from '@bms/common';

// ============================================================================
// Flavour Context
// ============================================================================

interface FlavourContextValue {
  /** Current flavour type (hospitality, residential, etc.) */
  flavour: FlavourType;
  /** Feature flags for the current flavour */
  features: FlavourFeatures;
  /** The raw space_category value from site config */
  spaceCategory: string;
  /** Site profile (full/simple) — needed by layouts */
  siteProfile: 'full' | 'simple';
  /** Resolved dashboard hierarchy settings (flavour default + site override) */
  dashboardSettings: DashboardSettings;
}

const FlavourContext = createContext<FlavourContextValue | null>(null);

/**
 * Access the current flavour context.
 * Must be used within FlavourProvider.
 */
export function useFlavour() {
  const ctx = useContext(FlavourContext);
  if (!ctx) throw new Error('useFlavour must be used within FlavourProvider');
  return ctx;
}

/**
 * Shortcut: check if a specific feature is enabled in current flavour
 */
export function useFeature<K extends keyof FlavourFeatures>(key: K): FlavourFeatures[K] {
  const { features } = useFlavour();
  return features[key];
}

// ============================================================================
// FlavourProvider
// ============================================================================

interface FlavourProviderProps {
  children: ReactNode;
}

export function FlavourProvider({ children }: FlavourProviderProps) {
  // Read spaceCategory from ThemeProvider (which fetches from /api/site/branding)
  const { spaceCategory } = useTheme();

  // Only fetch site config when authenticated (avoids 401 on login page)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: siteConfig } = useSiteConfig({ enabled: isAuthenticated });
  const siteProfile = (siteConfig?.profile ?? 'simple') as 'full' | 'simple';

  const value: FlavourContextValue = useMemo(() => {
    const features = getFlavourFeatures(spaceCategory);

    // Merge site-level dashboardSettings over flavour defaults
    const siteDs = (siteConfig?.settings as Record<string, unknown> | undefined)?.dashboardSettings as Partial<DashboardSettings> | undefined;
    const dashboardSettings: DashboardSettings = {
      ...features.dashboardHierarchy,
      ...siteDs,
    };

    // Override flavour feature with resolved settings
    const mergedFeatures: FlavourFeatures = {
      ...features,
      dashboardHierarchy: dashboardSettings,
    };

    return {
      flavour: getFlavourType(spaceCategory),
      features: mergedFeatures,
      spaceCategory,
      siteProfile,
      dashboardSettings,
    };
  }, [spaceCategory, siteConfig, siteProfile]);

  return (
    <FlavourContext.Provider value={value}>
      {children}
    </FlavourContext.Provider>
  );
}
